import * as React from 'react';
import clsx from 'clsx';
import { Box, Button, Paper } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { isEmpty } from 'lodash';
import { PRODUCTS_LOGOS } from '../../helpers/newDashboard';
import { renderHTMLString } from '../../helpers/helpers';
import { OnlineTrainingProduct } from './OnlineTrainingProduct';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useTypedSelector } from 'combineReducers';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import { useTranslation } from 'react-i18next';
import { fetchSubscriptions } from '../../slices/subscriptionsV2';
import { useFeature } from 'flagged';
import { fetchOnlineTrainingSSOUrl } from '../../slices/onlineTrainingSSO';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      minHeight: 350,
      position: 'relative',
      padding: theme.spacing(3),
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      marginBottom: theme.spacing(2)
    },
    containerDisabled: {
      opacity: 0.5
    },
    logo: {
      position: 'absolute',
      top: theme.spacing(2),
      right: theme.spacing(2)
    },
    title: {
      color: 'var(--on-surface-high-emphasis-000000-87-)',
      fontSize: 24,
      lineHeight: '24px',
      fontWeight: theme.typography.fontWeightRegular,
      letterSpacing: 0.18,
      marginBottom: theme.spacing(3)
    },
    titlePurchased: {
      marginBottom: theme.spacing(1)
    },
    subtitle: {
      color: 'var(--on-surface-high-emphasis-000000-87-)',
      fontSize: 14,
      lineHeight: '20px',
      fontWeight: theme.typography.fontWeightMedium,
      letterSpacing: 0.1,
      marginBottom: theme.spacing(3)
    },
    description: {
      color: 'var(--on-surface-high-emphasis-000000-87-)',
      fontSize: 14,
      lineHeight: '20px',
      fontWeight: theme.typography.fontWeightRegular,
      letterSpacing: 0.25,
      marginBottom: theme.spacing(3)
    },
    button: {
      letterSpacing: 1.25,
      paddingLeft: theme.spacing(1.5),
      paddingRight: theme.spacing(1.5),
      marginRight: theme.spacing(2)
    },
    purchased: {
      backgroundColor: 'var(--light-blue-surface-)'
    },
    purchasedIcon: {
      marginRight: theme.spacing(1)
    }
  })
);

export const TestLocators = {
  title: 'title',
  subtitle: 'subtitle',
  logo: 'logo',
  description: 'description',
  link: 'link'
};

export type ProductCard = {
  title: string;
  subtitle?: string;
  category: string;
  logo: string;
  description: string;
  id: string;
  type?: string;
  buttons: {
    label: string;
    url: string;
    targetType?: string;
  }[];
  disabled?: boolean;
  subscriptionTypePublicId?: number;
  purchased?: boolean;
  featureFlagId?: string;
};

type RouteParams = {
  organizationId: string;
  companyName: string;
};

const vcsId = 'vehicle-compliance-scheme';
const esgId = 'chas-social-sustainability';

export const ProductCard: React.FC<ProductCard> = ({
  title,
  subtitle,
  logo,
  description,
  buttons,
  type,
  disabled,
  purchased,
  id
}) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { organizationId } = useParams<RouteParams>();
  const esgProductEnabled = useFeature('AionEsgProductVisible');
  const vcsProductEnabled = useFeature('AionVcsProductVisible');
  const { credits: onlineTrainingCredits, isFetching: isFetchingCredits } = useTypedSelector(
    state => state.onlineTrainingCredits
  );
  const { SSOUrl: onlineTrainingSSOUrl, isFetching: isFetchingSSOUrl } = useTypedSelector(
    state => state.onlineTrainingSSO
  );
  const { userInfo } = useTypedSelector(state => state.userInfo);
  const { Breakdown, Credits, IsPending } = onlineTrainingCredits;
  const { organization } = useTypedSelector(state => state.organization);

  const Logo = PRODUCTS_LOGOS[logo];
  const hideEsgCard = id === esgId && !esgProductEnabled;
  const hideVcsCard = id === vcsId && !vcsProductEnabled;

  React.useEffect(() => {
    dispatch(fetchSubscriptions(organizationId));
  }, [dispatch, organizationId]);

  const handleOnlineTrainingLogin = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();
    userInfo &&
      dispatch(
        fetchOnlineTrainingSSOUrl({
          organizationId,
          userId: userInfo?.userId,
          afterSuccess: url => {
            window.location.replace(url);
          }
        })
      );
  };

  if (type === 'onlineTraining' && (!isEmpty(Breakdown) || Credits !== -1 || IsPending)) {
    return (
      <OnlineTrainingProduct
        credits={onlineTrainingCredits}
        SSOUrl={onlineTrainingSSOUrl}
        handleLogin={handleOnlineTrainingLogin}
        isLoading={isFetchingCredits || isFetchingSSOUrl}
      />
    );
  }

  const isPartnerLink = (link: string) => {
    return link.includes('partner');
  };

  const onLinkClick = (url: string, targetType: string) => () => {
    let link = url.replace('{organizationId}', organizationId);
    let target = targetType === 'internal' ? '_self' : '_blank';

    if (isPartnerLink(link) && organization !== null) {
      link = link.replace('{companyName}', organization.name);
    }

    window.open(link, target, 'noopener, noreferrer');
  };

  if (hideEsgCard || hideVcsCard) {
    return null;
  }

  return (
    <Paper
      id={id}
      classes={{ root: clsx(classes.container, disabled && classes.containerDisabled, purchased && classes.purchased) }}
    >
      <Box>
        <Typography
          variant="h6"
          classes={{ root: clsx(classes.title, purchased && classes.titlePurchased) }}
          data-testid={TestLocators.title}
        >
          {title}
        </Typography>
        {purchased && (
          <Box display="flex">
            <CheckCircleOutlineIcon className={classes.purchasedIcon} color="primary" />
            <Typography color="primary">{t('subscriber:productCard.purchased', 'Purchased')}</Typography>
          </Box>
        )}
        {Logo ? <Logo className={classes.logo} data-testid={TestLocators.logo} /> : null}
        <Typography variant="subtitle1" classes={{ root: classes.subtitle }} data-testid={TestLocators.subtitle}>
          {subtitle}
        </Typography>
        <Typography
          style={{ paddingRight: !!subtitle ? 0 : 60 }}
          variant="body1"
          classes={{ root: classes.description }}
          dangerouslySetInnerHTML={{
            __html: renderHTMLString(description)
          }}
          data-testid={TestLocators.description}
        />
      </Box>
      <Box display="flex">
        {purchased ? (
          <Button
            variant="outlined"
            color="primary"
            href={`/organizations/${organizationId}/membership`}
            classes={{ root: classes.button }}
          >
            {t('subscriber:productCard.toMembershipButton', 'Go to memberships')}
          </Button>
        ) : (
          buttons.map(({ label, url, targetType }, index) => (
            <Button
              key={index}
              color="primary"
              variant={disabled ? 'text' : index === 0 ? 'contained' : 'outlined'}
              classes={{ root: classes.button }}
              onClick={onLinkClick(url, targetType || '')}
              disabled={disabled}
              data-testid={TestLocators.link}
            >
              {label}
            </Button>
          ))
        )}
      </Box>
    </Paper>
  );
};
